import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  ApiError,
  eraseOrganization,
  getErasureHolds,
  listConnectedChats,
  revokeConnectedChat,
  type AuthenticatedSession,
  type ConnectedChat,
  type ErasureReport,
} from '../../api'
import { AdminPage, useAdminOrg } from './AdminPage'
import { disputeLockSwitchAtBilling } from '../../disputeLock'
import { ConfirmSheet } from '../../components/ConfirmSheet'
import { connectedLine, stillActs } from '../../connectedChats'

/**
 * What this organization is, where its other administrative pages are, and the
 * one action that ends it.
 */
export function Organization({ session }: { session: AuthenticatedSession }) {
  const { administered } = useAdminOrg(session)
  return (
    <AdminPage
      session={session}
      title="Organization"
      intro="Members, invitations, recipients, connected chats, and closing the organization."
    >
      {(orgId) => <OrgBody orgId={orgId} session={session} others={administered} />}
    </AdminPage>
  )
}

function OrgBody({
  orgId,
  session,
  others,
}: {
  orgId: string
  session: AuthenticatedSession
  others: AuthenticatedSession['memberships']
}) {
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [report, setReport] = useState<ErasureReport | null>(null)
  // null while asking; a failed question leaves it null, and the erasure
  // still says why it refuses if it does.
  const [disputeLock, setDisputeLock] = useState<boolean | null>(null)
  const org = session.memberships.find((m) => m.organization_id === orgId)!

  useEffect(() => {
    let live = true
    getErasureHolds(orgId)
      .then((h) => { if (live) setDisputeLock(h.dispute_lock) })
      .catch(() => {})
    return () => { live = false }
  }, [orgId])

  async function erase() {
    setBusy(true)
    setError('')
    try {
      const r = await eraseOrganization(orgId)
      setReport(r)
      setConfirming(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not erase that organization.')
    } finally {
      setBusy(false)
    }
  }

  if (report) {
    return (
      <div className="card">
        <h2>{org.organization_name} is erased</h2>
        <p className="small muted">
          {report.contacts} recipient{report.contacts === 1 ? '' : 's'} and {report.members} member
          {report.members === 1 ? '' : 's'} removed, and the documents of{' '}
          {report.envelopes.length} envelope{report.envelopes.length === 1 ? '' : 's'} destroyed.
        </p>
        {report.documents_remaining ? (
          <div className="notice notice-bad">
            {report.documents_remaining} stored file
            {report.documents_remaining === 1 ? '' : 's'} could not be removed. Every record is
            erased and the chain is correct; tell an operator, who can clear the files.
          </div>
        ) : null}
        <p className="small muted">
          The audit record remains, including who signed what.
        </p>
        <button className="btn" onClick={() => navigate('/')}>Back to envelopes</button>
      </div>
    )
  }

  return (
    <>
      {others.length > 1 && (
        <div className="card org-switch-rail" style={{ marginBottom: '1rem' }}>
          {/* Not .sidebar-section-label: the rail hides every one of those,
              and this list is shown only on the rail. */}
          <div className="org-switch-rail-label">You administer</div>
          <div className="org-switcher">
            {others.map((m) => (
              <Link
                key={m.organization_id}
                className={`btn btn-small${m.organization_id === orgId ? '' : ' btn-secondary'}`}
                to={`/orgs/${m.organization_id}/settings`}
              >
                {m.organization_name}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1rem' }}>
        <table className="table">
          <tbody>
            <tr><td className="muted">Name</td><td><strong>{org.organization_name}</strong></td></tr>
            <tr><td className="muted">Your role</td><td>Administrator</td></tr>
            <tr>
              <td className="muted">Identifier</td>
              <td className="mono small">{orgId}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="row-tight" style={{ flexWrap: 'wrap' }}>
          <Link className="btn btn-secondary" to={`/orgs/${orgId}/members`}>Members</Link>
          <Link className="btn btn-secondary" to={`/orgs/${orgId}/members/invitations`}>Invitations</Link>
          <Link className="btn btn-secondary" to={`/orgs/${orgId}/recipients`}>Recipients</Link>
          <Link className="btn btn-secondary" to={`/orgs/${orgId}/sign-in`}>Sign-in</Link>
        </div>
      </div>

      <ConnectedChats orgId={orgId} />

      {error && <div className="notice notice-bad" style={{ marginBottom: '.9rem' }}>{error}</div>}

      {/* Last, and visually separate, because it is the one thing on this page
          that cannot be undone. */}
      <CloseOrganization
        orgId={orgId}
        name={org.organization_name}
        disputeLock={disputeLock}
        switchAtBilling={disputeLockSwitchAtBilling(session, orgId)}
        supportEmail={session.support_email}
        onErase={() => setConfirming(true)}
      />

      {confirming && (
        <ConfirmSheet
          title={`Erase ${org.organization_name}?`}
          confirmLabel="Erase this organization"
          confirmWord={org.organization_name}
          busy={busy}
          error={error}
          onConfirm={() => void erase()}
          onClose={() => setConfirming(false)}
        >
          <p className="small muted">
            <strong>Removed:</strong> every contact and the addresses you hold for them, every
            template, every open invitation, every membership including your own, and the document
            files of every envelope.
          </p>
          <p className="small muted">
            <strong>Kept:</strong> the audit chain of each envelope and its certificate of
            completion: who signed, when, how they were authenticated, and the fingerprints.
          </p>
          <p className="small muted">This cannot be undone.</p>
        </ConfirmSheet>
      )}
    </>
  )
}

/**
 * The erasure, and what stops it. The dispute lock sentence is printed only
 * when the lock is on (GET /api/orgs/{id}/erasure). It was printed for every
 * organization, where it read as a refusal above a button that worked. While
 * the lock is on, Erase is disabled: it opened the confirmation sheet, had
 * the name typed, and only then met the server's 409. Billing, where the
 * switch is, is a link.
 */
export function CloseOrganization({
  orgId,
  name,
  disputeLock,
  switchAtBilling,
  supportEmail,
  onErase,
}: {
  orgId: string
  name: string
  disputeLock: boolean | null
  /** Whether Billing shows this organization's dispute lock switch. */
  switchAtBilling: boolean
  supportEmail?: string
  onErase: () => void
}) {
  return (
    <div className="card danger-zone">
      <h2>Close this organization</h2>
      <p className="small muted">
        Removes recipients, templates, invitations, memberships, and the document files. The audit record and certificates of completion are kept.
      </p>
      {disputeLock && (
        <p className="small muted" id="erase-locked">
          Dispute lock is on, so this organization cannot be erased.
          {switchAtBilling ? (
            <> Turn it off under <Link to={`/orgs/${orgId}/billing`}>Billing</Link> first.</>
          ) : supportEmail ? ` Write to ${supportEmail} and docsdone will help.` : ''}
        </p>
      )}
      <button
        className="btn btn-danger"
        type="button"
        disabled={disputeLock === true}
        aria-describedby={disputeLock ? 'erase-locked' : undefined}
        onClick={onErase}
      >
        Erase {name}
      </button>
    </div>
  )
}

function chatError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'not_an_admin':
        return 'Only an administrator can change connected chats.'
      case 'not_found':
        return 'No such connected chat.'
      case 'internal':
        return fallback
    }
  }
  return fallback
}

function ConnectedChats({ orgId }: { orgId: string }) {
  const [grants, setGrants] = useState<ConnectedChat[] | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState('')

  useEffect(() => {
    let live = true
    listConnectedChats(orgId)
      .then((r) => {
        if (live) setGrants(r.grants ?? [])
      })
      .catch((e: unknown) => {
        if (live) setError(chatError(e, 'Could not load connected chats.'))
      })
    return () => {
      live = false
    }
  }, [orgId])

  async function revoke(id: string) {
    setBusy(id)
    setError('')
    try {
      await revokeConnectedChat(orgId, id)
      setGrants((rows) => (rows ?? []).filter((row) => row.id !== id))
    } catch (e) {
      setError(chatError(e, 'Could not revoke that chat.'))
    } finally {
      setBusy('')
    }
  }

  return <ConnectedChatsView grants={grants} busy={busy} error={error} onRevoke={(id) => void revoke(id)} />
}

/**
 * An organization's connected chats. A chat's name is whatever the chat
 * called itself, up to 80 characters and possibly with nothing to break at,
 * so a row is laid out as the account page's are: the name wraps beside the
 * button, which keeps its width. As a .row-between of two blocks, a name with
 * no break pushed the page 228px sideways at 390px, and one with spaces
 * dropped Revoke onto a line of its own.
 */
export function ConnectedChatsView({ grants: listed, busy, error, onRevoke, now = Date.now() }: {
  /** null while asking. */
  grants: ConnectedChat[] | null
  busy: string
  error: string
  onRevoke: (id: string) => void
  now?: number
}) {
  // A chat whose 90 days have run out acts as nobody, and is not listed.
  const grants = listed && listed.filter((g) => stillActs(g, now))
  return (
    <div className="card" id="organization-chats" style={{ marginBottom: '1rem' }}>
      <h2>Connected chats</h2>
      <p className="small muted">
        A chat connected here acts as the person who connected it. It can see this organization's documents, and send, remind, withdraw, or replace a link; the chat asks that person before each of these, and docsdone cannot check that it does.
      </p>
      {error && <div className="notice notice-bad">{error}</div>}
      {grants === null && !error ? <p className="muted">Loading…</p> : null}
      {grants && grants.length === 0 ? (
        <>
          <p>No chat is connected.</p>
          <p className="small muted">A chat starts the connection, and you approve it in the browser.</p>
        </>
      ) : null}
      {grants && grants.length > 0 ? (
        <ul className="account-orgs">
          {grants.map((g) => (
            <li key={g.id} className="row-between">
              <span className="account-name">
                <strong>{g.client_name}</strong>
                <span className="small muted account-chat-when">{connectedLine(g)}</span>
              </span>
              <button
                className="btn btn-secondary btn-small"
                type="button"
                aria-label={`Revoke ${g.client_name}`}
                disabled={busy === g.id}
                onClick={() => onRevoke(g.id)}
              >
                {busy === g.id ? 'Revoking…' : 'Revoke'}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
